if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onMainQtyChange = this.onMainQtyChange.bind(this);
        this.onIntersection = this.onIntersection.bind(this);
        this.syncingQty = false;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.imageEl = this.querySelector('[data-sticky-image]');
        this.mediaEl = this.querySelector('.sticky-atc-bar__media');
        this.compareEl = this.querySelector('[data-sticky-compare]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.badgeEl = this.querySelector('[data-sticky-badge]');
        this.qtyInput = this.querySelector('[data-sticky-qty-input]');
        this.atcButton = this.querySelector('[data-sticky-atc]');
        this.atcText = this.querySelector('[data-sticky-atc-text]');
        this.spinner = this.querySelector('.sticky-atc-bar__spinner');

        try {
          this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '[]');
        } catch (e) {
          this.variants = [];
        }

        this.currentVariantId = this.getMainVariantId();
        this.bindEvents();
        this.setupObserver();
        this.syncFromMainQuantity();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantUnsubscriber?.();
        this.mainQtyInput?.removeEventListener('change', this.onMainQtyChange);
        this.mainQtyInput?.removeEventListener('input', this.onMainQtyChange);
      }

      get productForm() {
        return document.getElementById(this.productFormId);
      }

      get mainQtyInput() {
        return document.getElementById(`Quantity-${this.sectionId}`);
      }

      getMainVariantId() {
        const input = this.productForm?.querySelector('[name="id"]');
        return input ? Number(input.value) : this.variants[0]?.id;
      }

      bindEvents() {
        this.querySelector('[data-sticky-qty-minus]')?.addEventListener('click', () => this.changeQty(-1));
        this.querySelector('[data-sticky-qty-plus]')?.addEventListener('click', () => this.changeQty(1));
        this.qtyInput?.addEventListener('change', () => this.applyQty(this.qtyInput.value));
        this.atcButton?.addEventListener('click', () => this.addToCart());

        if (this.mainQtyInput) {
          this.mainQtyInput.addEventListener('change', this.onMainQtyChange);
          this.mainQtyInput.addEventListener('input', this.onMainQtyChange);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        // Fallback: watch hidden variant id input
        const variantInput = this.productForm?.querySelector('[name="id"]');
        variantInput?.addEventListener('change', () => {
          this.updateVariant(Number(variantInput.value));
        });
      }

      setupObserver() {
        const buttons =
          document.querySelector(`#MainProduct-${this.sectionId} .product-form__buttons`) ||
          document.querySelector('.product-form__buttons');

        if (!buttons || typeof IntersectionObserver === 'undefined') return;

        this.observer = new IntersectionObserver(this.onIntersection, {
          root: null,
          threshold: 0,
          rootMargin: '0px',
        });
        this.observer.observe(buttons);
      }

      onIntersection(entries) {
        const entry = entries[0];
        if (!entry) return;

        // Show only when original buttons have scrolled above the viewport
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle(scrolledPast);
      }

      toggle(show) {
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('hidden', !show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('sticky-atc-visible', show);
      }

      onVariantChange(event) {
        if (event?.data?.sectionId && String(event.data.sectionId) !== String(this.sectionId)) return;
        const variant = event?.data?.variant;
        if (!variant?.id) return;
        this.updateVariant(variant.id, variant);
      }

      updateVariant(variantId, liveVariant) {
        const variant = this.variants.find((v) => Number(v.id) === Number(variantId));
        this.currentVariantId = Number(variantId);

        if (!variant && !liveVariant) return;

        const available = variant?.available ?? liveVariant?.available ?? false;
        const priceFormatted = variant?.price_formatted;
        const compareFormatted = variant?.compare_at_price_formatted;
        const discount = variant?.discount_percent || 0;
        const compareAt = variant?.compare_at_price ?? liveVariant?.compare_at_price;
        const price = variant?.price ?? liveVariant?.price;
        const onSale = compareAt && price && compareAt > price;

        if (this.priceEl && priceFormatted) {
          this.priceEl.textContent = priceFormatted;
        }

        if (this.compareEl) {
          if (onSale && compareFormatted) {
            this.compareEl.textContent = compareFormatted;
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
            this.compareEl.textContent = '';
          }
        }

        if (this.badgeEl) {
          if (onSale && discount > 0) {
            this.badgeEl.textContent = `${discount}% OFF`;
            this.badgeEl.hidden = false;
          } else {
            this.badgeEl.hidden = true;
            this.badgeEl.textContent = '';
          }
        }

        const imageUrl = variant?.image;
        if (this.imageEl && imageUrl) {
          this.imageEl.src = imageUrl;
          this.imageEl.hidden = false;
          if (this.mediaEl) this.mediaEl.hidden = false;
        }

        if (variant) {
          this.qtyInput.min = variant.qty_min || 1;
          this.qtyInput.step = variant.qty_step || 1;
          if (variant.qty_max != null) {
            this.qtyInput.max = variant.qty_max;
          } else {
            this.qtyInput.removeAttribute('max');
          }
          this.applyQty(this.qtyInput.value);
        }

        this.setAvailability(available);
      }

      setAvailability(available) {
        if (!this.atcButton || !this.atcText) return;
        if (available) {
          this.atcButton.removeAttribute('disabled');
          this.atcText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.atcButton.setAttribute('disabled', 'disabled');
          this.atcText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }

      changeQty(direction) {
        const step = Number(this.qtyInput.step) || 1;
        const min = Number(this.qtyInput.min) || 1;
        const max = this.qtyInput.max !== '' ? Number(this.qtyInput.max) : null;
        let next = Number(this.qtyInput.value || min) + direction * step;
        if (next < min) next = min;
        if (max != null && next > max) next = max;
        this.applyQty(next);
      }

      applyQty(value) {
        const min = Number(this.qtyInput.min) || 1;
        const max = this.qtyInput.max !== '' ? Number(this.qtyInput.max) : null;
        const step = Number(this.qtyInput.step) || 1;
        let qty = Number(value);

        if (Number.isNaN(qty) || qty < min) qty = min;
        if (max != null && qty > max) qty = max;

        // Snap to step relative to min
        qty = min + Math.round((qty - min) / step) * step;
        if (qty < min) qty = min;

        this.qtyInput.value = qty;

        if (this.syncingQty) return;
        this.syncingQty = true;
        if (this.mainQtyInput) {
          this.mainQtyInput.value = qty;
          this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.syncingQty = false;
      }

      onMainQtyChange() {
        if (this.syncingQty || !this.mainQtyInput) return;
        this.syncingQty = true;
        this.qtyInput.value = this.mainQtyInput.value;
        this.syncingQty = false;
      }

      syncFromMainQuantity() {
        if (this.mainQtyInput?.value) {
          this.qtyInput.value = this.mainQtyInput.value;
        }
      }

      setLoading(loading) {
        this.atcButton?.classList.toggle('loading', loading);
        this.atcButton?.toggleAttribute('aria-disabled', loading);
        this.spinner?.classList.toggle('hidden', !loading);
      }

      addToCart() {
        if (this.atcButton?.hasAttribute('disabled') || this.atcButton?.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const form = this.productForm;
        if (!form) return;

        // Keep main form qty + variant in sync, then reuse product-form AJAX + cart drawer
        this.applyQty(this.qtyInput.value);

        const variantInput = form.querySelector('[name="id"]');
        if (variantInput && this.currentVariantId) {
          variantInput.value = this.currentVariantId;
        }

        this.setLoading(true);

        const productFormEl = form.closest('product-form');
        const submitButton = productFormEl?.querySelector('[type="submit"]');

        const resetLoading = () => this.setLoading(false);

        // product-form.js handles fetch + cart drawer; listen once for completion
        const onCartUpdate = () => {
          resetLoading();
          if (typeof this.cartUpdateUnsub === 'function') this.cartUpdateUnsub();
        };
        const onCartError = () => {
          resetLoading();
          if (typeof this.cartErrorUnsub === 'function') this.cartErrorUnsub();
        };

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
          this.cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, onCartError);
        } else {
          setTimeout(resetLoading, 2000);
        }

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitButton || undefined);
        } else if (submitButton) {
          submitButton.click();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }

        // Safety: spinner shouldn't hang if pub/sub missed
        setTimeout(() => {
          if (this.atcButton?.classList.contains('loading')) resetLoading();
        }, 4000);
      }
    }
  );
}
